import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { SchoolService } from '@services/school.service';
import {
  BehaviorSubject,
  defer,
  interval,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  share,
  shareReplay,
  startWith,
  take,
  tap,
  switchMap,
  timeout,
} from 'rxjs/operators';
import { environment } from '@env/environment';
import { IframeService } from './iframe.service';
import {
  ScaffoldDialogRef,
  ScaffoldDialogService,
} from '@app/framework/dialog/scaffold-dialog.service';
import { MfaModalComponent } from '@app/account/mfa-modal/mfa-modal.component';
import { timer } from 'rxjs';
import { NotificationsService } from './notifications.service';
import { Router } from '@angular/router';
import { OverlayService } from './overlay.service';
import { WebSocketService } from './web-socket.service';
import { ProfileDetailsService } from '@app/account/services/profile-details.service';
import { CognitoDomainService } from '@services/cognito-domain.service';
import * as CryptoJS from 'crypto-js';
import * as Sentry from '@sentry/angular';

/**
 * Where a user was headed before being bounced to login. Deliberately separate
 * from 'redirectUrl' because clearLocalStorage() runs on the logOut() that
 * HttpUtilService triggers for failed auth, which would erase the destination
 * before the SSO round trip could use it.
 */
export const PENDING_REDIRECT_URL_KEY = 'pendingRedirectUrl';

/**
 * Login / SSO handoff pages that must not be recorded as a return destination.
 * Path-prefix match so authenticated destinations like /admin/sso are kept.
 */
export function isSsoFlowUrl(url: string): boolean {
  const path = (url || '').split('?')[0];
  return /^\/sso(\/|$)/.test(path);
}

export function isAuthFlowUrl(url: string): boolean {
  const path = (url || '').split('?')[0];
  return /^\/(direct-)?login(\/|$)/.test(path) || isSsoFlowUrl(path);
}

export class ScaffoldUser {
  /**The users id*/
  user_id: string;
  /** Username that is tied to the cognito authentication service*/
  cognito_username: any;
  /** The role of the user in the instance*/
  role: string;
  [key: string]: any;
}

/** Server-signed Intercom Messenger identity from GET /users/self?intercom_user_hash=1 */
export interface IntercomUserHash {
  user_id: string;
  email?: string;
  intercom_user_jwt: string;
  expiresIn?: string | number;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  accessToken;
  refreshToken;
  accessLoading = false;
  refershToken;
  _loggedIn = false;
  mapFields = {
    given_name: 'first_name',
    family_name: 'last_name',
  };
  hasAttemptedCognitoAttributes: boolean;
  cognitoAttributes = new BehaviorSubject(null);
  hasCheckedMfa: any;
  private mfaEvaluationInFlight = false;
  private userPoolMfaOptional$: Observable<boolean> | null = null;
  private loginMfaSetupRequired = new BehaviorSubject(false);
  loginMfaSetupRequired$ = this.loginMfaSetupRequired.asObservable();
  set loggedIn(value) {
    if (value !== this.loggedIn) {
      console.log('LOGGED IN:', value);
      this._loggedIn = value;
      this.logInState.next(value);
      if (value) {
        this.subscribeToUserAccountMessages();
      } else {
        this.unsubscribeFromUserAccountMessages();
      }
    }
  }
  get loggedIn() {
    return this._loggedIn;
  }
  id: string;
  logInState = new BehaviorSubject<boolean>(false);
  authSub = new Subject<any>();
  userInfo = null;
  private _roleInformation: ScaffoldUser | null;
  private refreshSessionTime = 50;
  private _roleDetailsLoaded = false;
  private _roleDetails = new Subject<ScaffoldUser>();
  private roleDetailsLoader: Observable<any> | null;
  private roleDetailsLoaderSubscription: Subscription | null;
  sessionDetails: any; //holds any details about the current session
  // Used to pass variables between pages
  // /login -> /new-password-required | { currentAttributes : {}, requiredAttributes : [] }
  userAttributes = {
    currentAttributes: {},
    requiredAttributes: [],
  };
  private refreshTokenTimer: Subscription;
  private authBlockTimer: Subscription;
  private userAccountSubscription: Subscription;
  private webSocketService: WebSocketService;

  constructor(
    public notifications: NotificationsService,
    public dialog: ScaffoldDialogService,
    private schoolService: SchoolService,
    private cookieService: CookieService,
    private http: HttpClient,
    private iframeService: IframeService,
    private router: Router,
    private overlayService: OverlayService,
    private injector: Injector
  ) {
    this.startRefreshTokenTimer();
  }

  subscribeToUserAccountMessages(): void {
    if (!this.webSocketService) {
      this.webSocketService = this.injector.get(WebSocketService);
    }

    this.userAccountSubscription?.unsubscribe();

    this.userAccountSubscription = this.webSocketService
      .subscribeToMessages('user_account')
      .subscribe(response => {
        switch (response.message) {
          case 'disabled': {
            this.notifications.popNotification(
              'Account Disabled',
              'Your account has been disabled.'
            );
            this.logOut().subscribe(() => {
              this.router.navigate(['/login']);
            });
            break;
          }
          case 'logout': {
            this.resetUserAuthDetails();
            this.router.navigate(['/login']);
            break;
          }
        }
      });
  }

  unsubscribeFromUserAccountMessages(): void {
    this.userAccountSubscription?.unsubscribe();
    this.userAccountSubscription = null;
  }

  // call to get new access token before it expires every 50 mins, should be reset everytime access token has been set. Will not be called if no access token found.
  public startRefreshTokenTimer(minutes?, immediateCheck?) {
    console.log('minutes', minutes);
    if (minutes) this.refreshSessionTime = minutes;
    this.refreshTokenTimer?.unsubscribe();
    const timer$ = timer(this.refreshSessionTime * 60 * 1000);

    this.refreshTokenTimer = (
      immediateCheck ? timer$.pipe(startWith(0)) : timer$
    )
      .pipe(
        tap(() => {
          console.log('REFRESH?');
        }),
        switchMap(() => (this.accessToken ? this.refreshSession() : of()))
      )
      .subscribe();
  }

  watchAuthenticationCookie() {
    this.logInState.subscribe(loggedIn => {
      if (loggedIn) {
        this.authBlockTimer?.unsubscribe();
        this.authBlockTimer = interval(2000).subscribe(() => {
          if (
            !this.cookieService.get('authenticated') &&
            !this.cookieService.get('active_session')
          ) {
            this.router.navigate(['/login']);
          }
        });
      } else {
        this.authBlockTimer?.unsubscribe();
      }
    });
  }

  // values directly from cognito for the logged in user
  getCognitoUserAttributes$(refresh?) {
    if (!this.hasAttemptedCognitoAttributes || refresh) {
      this.hasAttemptedCognitoAttributes = true;
      this.http
        .request('get', `${environment.apiRoot}/auth/userattributes`)
        .pipe(catchError(error => of({ error })))
        .pipe(
          tap(response =>
            this.cognitoAttributes.next(this.mapUserAttributes(response))
          )
        )
        .subscribe();
    }
    return this.cognitoAttributes.asObservable();
  }

  // map values from cognito to our own expected keys and  values
  mapUserAttributes(attributes) {
    return !Array.isArray(attributes?.details?.UserAttributes)
      ? {}
      : attributes?.details?.UserAttributes.reduce(
          (acc, attribute) => {
            const key = this.mapFields[attribute.Name] || attribute.Name;
            acc[key] = attribute.Value;
            if (key.includes('_verified')) {
              acc[key] = acc[key] === 'true';
            }
            return acc;
          },
          { cognito_username: attributes?.details.Username }
        );
  }

  /** Store temporary values related to the user */
  getUserAttributes() {
    if (this.sessionDetails?.userAttributes) {
      const attributes = {};
      for (let key in this.sessionDetails.userAttributes) {
        let attribute = this.sessionDetails.userAttributes[key];
        // strip imutable _verified from attributes to set
        if (!key.includes('_verified')) {
          attributes[key] = attribute;
        }
      }
      this.userAttributes.currentAttributes = attributes;
      this.userAttributes.requiredAttributes =
        this.sessionDetails.requiredAttributes;
    }
    return this.userAttributes;
  }

  /** @description Check if user is logged in
   * Will refresh access token if it is expired or missing
   */
  isLoggedIn(accessToken?, refreshToken?): Observable<boolean> {
    let observer = new Subject<boolean>();
    setTimeout(() => {
      let logged;
      logged =
        this.cookieService.get('authenticated') === 'true' &&
        this.cookieService.get('active_session') === 'true';
      if (logged) {
        this.loggedIn = true;
        //populate role details if they are not cached

        if (!this.accessToken) {
          this.getAuthenitcation()
            .pipe(take(1))
            .subscribe(at => {
              observer.next(true);
              observer.complete();
            });
        } else {
          observer.next(true);
          observer.complete();
        }
      } else {
        this.accessToken = null;
        // authenticated cookie is missing, check for refresh
        // attemptRefresh
        this.refreshSession().subscribe(ref => {
          console.log('REFRESH:', ref);
          if (!ref) {
            observer.next(false);
            observer.complete();
          } else {
            if (!this.accessToken) {
              this.getAuthenitcation()
                .pipe(take(1))
                .subscribe(at => {
                  observer.next(true);
                  observer.complete();
                });
            } else {
              observer.next(true);
              observer.complete();
            }
          }
        });
      }
    }, 100);
    return observer.asObservable();
  }

  /**
   *@description Sets cookies needed to determine origin of request,
   * Extra safety net to set cognito values if they go missing between login refreshes
   * */
  setCookies() {
    const poolId = this.cookieService.get('cog_user_pool_id');
    const clientId = this.cookieService.get('cog_client_id');
    const schoolName = this.cookieService.get('school_name');
    const schoolDomain = this.cookieService.get('school_domain');
    if (!poolId) {
      this.setCookie('cog_user_pool_id', environment.cognitoUserPoolId);
    }
    if (!clientId) {
      this.setCookie('cog_client_id', environment.cognitoClientId);
    }
    if (!schoolName) {
      this.setCookie('school_name', environment.name);
    }
    if (!schoolDomain) {
      this.setCookie('school_domain', environment.domain);
    }
  }
  /**
   * @description Sets a cookie, used for readability of code
   * @param cookie
   * @param value
   */
  setCookie(cookie, value) {
    this.cookieService.set(
      cookie,
      value,
      null,
      '/',
      environment.cookieDomain,
      true
    );
  }

  /**@description Refreshes the session if the access token or authenticated cookie is expired or missing
   * */
  refreshSession(): Observable<any> {
    this.startRefreshTokenTimer();
    let observer = new Subject<any>();
    this.http
      .post(environment.apiRoot + '/auth/refresh', {
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
      })
      .subscribe(
        (data: any) => {
          if (data.status === 'refreshed') {
            console.log('UserService : Refresh success');
            // set a cookie to see if the session has ended since the last login attemps. this is to allow for the session details to be in an httponly cookie
            this.cookieService.set('active_session', 'true', null, '/');

            //populate role details if they are not cached
            this.getRoleDetails().subscribe(role => {});
            this.setUserInfo(data.details.userInfo);
            if (data.access) {
              this.accessToken = data.access;
            }
            this.loggedIn = true;
            observer.next(true);
            observer.complete();
          } else {
            console.log('UserService : Refresh failed');
            this.loggedIn = false;
            observer.next(false);
            observer.complete();
            this.router.navigate(['/login']);
          }
        },
        err => {
          console.log('UserService : Refresh failed', err);
          this.loggedIn = false;
          observer.next(false);
          this.router.navigate(['/login']);
          observer.complete();
        }
      );

    return observer.asObservable();
  }

  /**
   * @description Log in a user
   * @param username
   * @param password
   */
  login(username: string, password: string): Observable<any> {
    let url = '/auth/logininit';
    if (this.router.url.includes('direct-login')) url += '?check_for_sso=true';
    if (!this.iframeService.isInIframe) {
      console.log('DELETE COOKIE');
      this.cookieService.delete('t4ntn033dd');
    }
    this.setCookies();
    let login = new Subject<any>();
    this.http
      .post(environment.apiRoot + url, { username, password })
      .subscribe((data: any) => {
        if (data.challengeName) {
          return login.next(data);
        }
        if (data.userAttributes) {
          data.userAttributes.userName = username;
          this.sessionDetails = data.userAttributes;
        }
        if (data?.details?.userInfo) {
          Object.keys(data?.details?.userInfo?.data || {}).forEach(key => {
            if (this.mapFields[key]) {
              data.details.userInfo.data[this.mapFields[key]] =
                this.mapFields[key];
              delete data.details.userInfo.data[key];
            }
          });
          this.setUserInfo(data.details.userInfo);
        }
        // ensure that logouts are not directed to cogntio domain
        this.cookieService.set('authenticated_sso', 'false', null, '/');
        // set a cookie to see if the sessionhas ended since the last login attemps. this is to allow for the session details to be in an httponly cookie
        this.cookieService.set('active_session', 'true', null, '/');
        if (data.access) {
          this.startRefreshTokenTimer();
          this.accessToken = data.access;
        }
        if (data.status === 'logged in') {
          this.loggedIn = true;
        }
        login.next(data);
        login.complete();
      });
    return login.asObservable();
  }

  submitChallengeSelection(username, selection, session): Observable<any> {
    return this.http
      .post(environment.apiRoot + '/auth/mfachallengeselection', {
        username,
        selection,
        session,
      })
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap(response => {
          if (!response || response.error) {
            this.notifications.popNotification(
              '',
              'Unknown error while making MFA selection'
            );
          }
        })
      );
  }

  handleChallenge(username, usercode, session, challengeType): Observable<any> {
    // avoid any white spaces in user provided code, will not match if provided
    usercode = usercode?.replace(/\s/g, '');
    return this.http
      .post(environment.apiRoot + `/auth/${challengeType}challenge`, {
        username,
        usercode,
        session,
      })
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap(response => {
          if (response.access) {
            // avoid sso logout
            this.cookieService.set('authenticated_sso', 'false', null, '/');
            // set a cookie to see if the sessionhas ended since the last login attemps. this is to allow for the session details to be in an httponly cookie
            this.cookieService.set('active_session', 'true', null, '/');
            this.startRefreshTokenTimer();
            this.accessToken = response.access;
          }

          if (response?.userInfo) {
            this.loggedIn = true;
            Object.keys(response.userInfo.data).forEach(key => {
              if (this.mapFields[key]) {
                response.userInfo.data[this.mapFields[key]] =
                  this.mapFields[key];
                delete response.userInfo.data[key];
              }
            });
            this.setUserInfo(response.userInfo);
          }
          if (response.userAttributes) {
            this.sessionDetails = response.userAttributes;
          }
        })
      );
  }

  /**
   * Watch the login state of the current user
   * */
  getLogInState(): Observable<any> {
    return this.logInState.asObservable();
  }

  resetUserAuthDetails() {
    this.userInfo = null;
    this.clearPersistedUserInfo();
    this.stopSentrySession();
    this.accessToken = null;
    this.sessionDetails = null;
    this.clearLegacyUserInfoCookie();
    this.cookieService.delete('authenticated');
    this.cookieService.delete('active_session');
    if (this.roleDetailsLoaderSubscription) {
      this.roleDetailsLoaderSubscription.unsubscribe();
      this.roleDetailsLoaderSubscription = null;
    }
    this.roleDetailsLoader = null;
    this._roleDetailsLoaded = false;
    this._roleInformation = null;
    this.setCookie('cog_user_pool_id', environment.cognitoUserPoolId);
    this.setCookie('cog_client_id', environment.cognitoClientId);
    this.setCookie('school_name', environment.name);
    this.setCookie('school_domain', environment.domain);
    this.clearLocalStorage();
    this.injector.get(ProfileDetailsService).clearCache();
    this.hasCheckedMfa = false;
    this.mfaEvaluationInFlight = false;
    this.userPoolMfaOptional$ = null;
    this.setLoginMfaSetupRequired(false);
    this.loggedIn = false;
  }

  /** Log out the current user and clear all user data */
  logOut() {
    // Clear everything
    const logoutSub = new Subject();
    this.http.post(environment.apiRoot + '/auth/logout', {}).subscribe({
      next: () => {
        this.resetUserAuthDetails();
        logoutSub.next('loggedout');
      },
      error: () => {
        this.resetUserAuthDetails();
        logoutSub.next('loggedout');
      },
    });
    return logoutSub.asObservable();
  }
  /**
   * @description Clear the local storage of the current user
   */
  clearLocalStorage() {
    let keysToClear = [
      'redirectUrl',
      'brightspace_auth',
      '_scaffold_active_feature',
    ];
    keysToClear.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /** Gett the password ploicy for the current instance */
  getPasswordPolicy() {
    // Circular dependency on HttpUtilService, use HttpClient directly
    return this.http.get(environment.apiRoot + '/password-policy');
  }

  /**
   * Manually change the current user password
   */
  changePassword(oldPassword, newPassword): Observable<any> {
    return this.isLoggedIn().pipe(
      mergeMap(loggedIn => {
        return Observable.create(observer => {
          if (loggedIn) {
            this.http
              .post(environment.apiRoot + '/auth/resetpassword', {
                oldPassword,
                newPassword,
              })
              .subscribe(
                result => {
                  console.log(result);
                  // reset session?
                  observer.next(result);
                  observer.complete();
                },
                err => {
                  observer.error(err);
                  observer.complete();
                }
              );
          } else {
            observer.error();
            observer.complete();
          }
        });
      })
    );
  }
  /**
   * Trigger forgot password event
   * @param username User to update password
   */
  triggerForgotPassword(username): Observable<any> {
    let params: { username: string; url?: string } = { username };
    return Observable.create(observer => {
      const currentSubdomain = window.location.hostname.split('.')[0];
      if (currentSubdomain !== environment.domain) {
        params.url =
          window.location.hostname === 'localhost'
            ? 'localhost:4200'
            : window.location.hostname;
      }
      console.log('Forgot PW Triggered', params);
      this.http
        .post(environment.apiRoot + '/auth/forgotpassword', params)
        .subscribe(
          (data: any) => {
            console.log('Forgot PW Triggered');
            observer.next(data);
            observer.complete();
          },
          err => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Used for unauthed users (forgot password flow)*/
  confirmPassword(verificationCode, newPassword, username) {
    return Observable.create(observer => {
      this.http
        .post(environment.apiRoot + '/auth/verifypassword', {
          verificationCode,
          newPassword,
          username,
        })
        .subscribe({
          next: body => {
            if (body && (body as any).error) {
              observer.error({ error: body });
              observer.complete();
              return;
            }
            observer.next();
            observer.complete();
          },
          error: err => {
            observer.error(err);
            observer.complete();
          },
        });
    });
  }

  /** Authed users using a temp pw on their first login*/
  completeNewPasswordChallenge(newPassword, oldPassword, attributes) {
    return Observable.create(observer => {
      this.http
        .post(environment.apiRoot + '/auth/completenewpassword', {
          attributes,
          newPassword,
          oldPassword,
          userName: this.sessionDetails.userName,
        })
        .subscribe(
          (data: any) => {
            if (data.status === 'password reset') {
              this.getRoleDetails().subscribe(role => {});
              if (data.userAttributes) {
                this.sessionDetails = data.userAttributes;
              }
              if (data?.details?.userInfo) {
                this.setUserInfo(data.details.userInfo);
              }
              if (data.access) {
                this.startRefreshTokenTimer();
                this.accessToken = data.access;
              }
              observer.next('Successful Login');
              observer.complete();
            } else {
              observer.error(data.error);
              observer.complete();
            }
          },
          err => {
            // Something bad happened
            observer.error(err);
            observer.complete();
          }
        );
    });
  }
  private userInfoStorageKey(): string {
    return `scaffold_user_info:${environment.domain}:${environment.cognitoUserPoolId}`;
  }

  private userInfoEncryptionKey(): string {
    return `${environment.domain}:${environment.cognitoUserPoolId}:user_info`;
  }

  private persistUserInfo(userInfo: unknown): void {
    try {
      const plain = JSON.stringify(userInfo);
      const cipher = CryptoJS.AES.encrypt(
        plain,
        this.userInfoEncryptionKey()
      ).toString();
      localStorage.setItem(this.userInfoStorageKey(), cipher);
    } catch {}
  }

  private clearLegacyUserInfoCookie(): void {
    this.cookieService.delete(
      'user_info',
      '/',
      environment.cookieDomain,
      environment.secureCookie
    );
  }

  private clearPersistedUserInfo(): void {
    try {
      localStorage.removeItem(this.userInfoStorageKey());
    } catch {}
  }

  private loadPersistedUserInfo(): any {
    try {
      const raw = localStorage.getItem(this.userInfoStorageKey());
      if (raw != null && raw.length > 0) {
        const bytes = CryptoJS.AES.decrypt(raw, this.userInfoEncryptionKey());
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (!decrypted) {
          this.clearPersistedUserInfo();
          return null;
        }
        return JSON.parse(decrypted);
      }
    } catch {
      this.clearPersistedUserInfo();
    }
    return null;
  }

  /** Get details about the logged in user */
  getUserInfo() {
    if (!this.userInfo) {
      const sessionActive =
        this.cookieService.get('authenticated') === 'true' &&
        this.cookieService.get('active_session') === 'true';
      if (sessionActive) {
        const fromStorage = this.loadPersistedUserInfo();
        if (fromStorage) {
          this.userInfo = fromStorage;
        }
      }
    }
    return this.userInfo;
  }

  isUserMfaSet(): boolean {
    if (this.getUserInfo()?.mfa_set === true) {
      return true;
    }
    return this._roleInformation?.data?.mfa_set === true;
  }
  /** Clear current user information */
  resetUserInfo() {
    return Observable.create(observer => {
      this.userInfo = null;
      this.clearPersistedUserInfo();
      this.http
        .post(environment.apiRoot + '/auth/userattributes', {})
        .subscribe((data: any) => {
          const attributes = {};
          data.details?.UserAttributes?.forEach(attribute => {
            // _verified is coming back as string "true | false " convert to a boolean
            if (attribute.Name.includes('_verified')) {
              attributes[attribute.Name] = attribute.Value == 'true';
            } else {
              attributes[attribute.Name] = attribute.Value;
            }
          });
          if (Object.keys(attributes).length > 0) {
            attributes['username'] = data.details?.Username;
            this.setUserInfo(attributes);
          }
          observer.next(this.userInfo);
          observer.complete();
        });
    });
  }
  /**
   * Set the details about the logged in user
   * @param userInfo Details to set
   */
  setUserInfo(userInfo) {
    if (userInfo) {
      let email = userInfo?.email || userInfo?.data?.email;
      let username = userInfo?.cognito_username;
      let role = userInfo?.role;
      this.startSentrySession({
        email: email,
        username: username,
        role: role,
      });

      this.userInfo = userInfo;
      this.persistUserInfo(userInfo);
      this.clearLegacyUserInfoCookie();
    }
  }

  startSentrySession(info) {
    console.log('starting new sentry session');
    Sentry.setUser({
      email: info.email,
      username: info.username,
    });
    Sentry.setTags({
      role: info.role,
    });
    // Get the replay instance
    const replay = Sentry.getReplay();
    if (replay) {
      replay.start(); // Manually stops the current session
    }
  }
  stopSentrySession() {
    console.log('stopping sentry session');
    // Get the replay instance
    const replay = Sentry.getReplay();

    if (replay) {
      replay.stop(); // Manually stops the current session
    }
    Sentry.setUser({});
  }

  /**
   * Get verification code for contat method
   * @param contactType Type to verify
   */
  getVerificationCode(contactType: 'email'): Observable<any> {
    return this.http
      .post(environment.apiRoot + '/auth/sendverification', {
        contactType,
      })
      .pipe(share());
  }
  /**
   * Attempt to verify submitted code
   * @param contactType Type of contact
   * @param code Verification code
   */
  sendVerficationCode(contactType: 'email', code): Observable<any> {
    return this.http
      .post(environment.apiRoot + '/auth/submitverfication', {
        contactType,
        code,
      })
      .pipe(share());
  }

  private maybeCheckForMfaEnabledAfterRoleLoad(skipMfaCheck?: boolean) {
    if (
      skipMfaCheck ||
      this.isOnVerifyPhoneAndEmailRoute() ||
      this.mfaEvaluationInFlight
    ) {
      return;
    }
    this.checkForMfaEnabled();
  }

  /**
   * Server-signed Intercom identity for Messenger boot (SEC-85).
   */
  getIntercomUserHash$(): Observable<IntercomUserHash | null> {
    return this.logInState.pipe(
      filter(loggedIn => loggedIn),
      take(1),
      switchMap(() =>
        this.http
          .get<{ intercom_user_hash?: IntercomUserHash }>(
            `${environment.apiRoot}/users/self?intercom_user_hash=1`
          )
          .pipe(
            map(response => response?.intercom_user_hash ?? null),
            catchError(err => {
              console.log(err);
              return of(null);
            })
          )
      )
    );
  }

  /**
   * Get deatils about the role and permissions of the current user
   * @param refresh Force refreshing the details from the API instead of using the localy stored data
   */
  getRoleDetails(
    refresh?: boolean,
    options?: { skipMfaCheck?: boolean }
  ): Observable<ScaffoldUser> {
    return this.logInState.pipe(
      filter(loggedIn => loggedIn), // Only proceed if loggedIn is true
      switchMap(() => {
        if (refresh) {
          this._roleDetailsLoaded = false;
          this._roleInformation = null;
          if (this.roleDetailsLoaderSubscription) {
            this.roleDetailsLoaderSubscription.unsubscribe();
            this.roleDetailsLoaderSubscription = null;
          }
          this.roleDetailsLoader = null;
        }

        if (this._roleDetailsLoaded) {
          if (this._roleInformation) {
            // Emit current then future updates (e.g. from updateRoleInformation())
            return merge(
              of(this._roleInformation),
              this._roleDetails.asObservable()
            );
          } else {
            return this._roleDetails.asObservable();
          }
        } else {
          // get role details from api
          if (!this.roleDetailsLoader) {
            const skipMfaCheck = options?.skipMfaCheck;
            this.roleDetailsLoader = this.http.get(
              environment.apiRoot + '/users/self?role_details=true'
            );
            this.roleDetailsLoaderSubscription = this.roleDetailsLoader
              .pipe(take(1))
              .subscribe({
                next: (data: ScaffoldUser) => {
                  this.roleDetailsLoader = null;
                  this.roleDetailsLoaderSubscription = null;
                  // role details have been checked
                  this._roleDetailsLoaded = true;
                  this._roleInformation = data;
                  this._roleDetails.next(data);
                  this.maybeCheckForMfaEnabledAfterRoleLoad(skipMfaCheck);
                },
                error: err => {
                  this.roleDetailsLoader = null;
                  this.roleDetailsLoaderSubscription = null;
                  this._roleDetailsLoaded = false;
                  this._roleInformation = null;
                  console.log(err);
                },
              });
          }
          return this._roleDetails.asObservable();
        }
      })
    );
  }

  getUserInstances(inst): Observable<any> {
    const result = new Subject<any>();
    const promises = [];
    let instances = [];
    inst.forEach(instance => {
      const promise = new Promise((resolve, reject) => {
        // Temporarily set the sub_domain cookie to the instance's sub_domain
        this.http
          .get(
            environment.apiRoot +
              '/users/self?role_details=true&sub_domain=' +
              instance.sub_domain +
              '&environment=' +
              instance.environment
          )
          .subscribe(
            (data: ScaffoldUser) => {
              console.log(data);
              if (data.user_id) {
                instances.push(instance.sub_domain);
                resolve(true);
              } else {
                resolve(false);
              }
            },
            err => {
              console.log(err);
            }
          );
      });
      promises.push(promise);
    });
    Promise.all(promises).then(() => {
      result.next(instances);
      result.complete();
    });
    return result.asObservable();
  }

  updateRoleInformation() {
    this.http
      .get(environment.apiRoot + '/users/self?role_details=true')
      .subscribe(
        (data: ScaffoldUser) => {
          this._roleDetailsLoaded = true;
          this._roleInformation = data;
          this._roleDetails.next(data);
          this.maybeCheckForMfaEnabledAfterRoleLoad();
        },
        err => {
          console.log(err);
        }
      );
  }

  setLoginMfaSetupRequired(required: boolean) {
    this.loginMfaSetupRequired.next(required);
    document.body.classList.toggle('mfa-setup-required', required);
  }

  private isLoginMfaSetupBlocking(): boolean {
    const mfaSet =
      this.userInfo?.mfa_set ?? this._roleInformation?.data?.mfa_set;
    return this.loginMfaSetupRequired.value && mfaSet !== true;
  }

  private isOnVerifyPhoneAndEmailRoute(): boolean {
    return this.router.url.includes('verify-phone-and-email');
  }

  requestVerifyRequiredMfaSetupDialog(): Observable<
    ScaffoldDialogRef | undefined
  > {
    return defer(() => {
      this.resetMfaEligibilityState();
      this.mfaEvaluationInFlight = true;
      return this.isSsoEnabled$().pipe(
        take(1),
        switchMap(isSsoEnabled => {
          if (isSsoEnabled) {
            return of(undefined);
          }
          return this.getRoleDetails(true, { skipMfaCheck: true }).pipe(
            filter((role): role is ScaffoldUser => !!role),
            take(1),
            timeout(15000),
            catchError(() => of(null)),
            switchMap(role =>
              role
                ? this.getMfaSetupEligibility$({
                    treatCheckFailureAsNeedsSetup: true,
                  })
                : of(false)
            ),
            map(needsSetup =>
              needsSetup
                ? this.openRequiredMfaSetupDialog({
                    initialView: 'mfa',
                    allowOnVerifyRoute: true,
                    suppressAppShellBlock: true,
                  })
                : undefined
            )
          );
        }),
        catchError(() => of(undefined))
      );
    }).pipe(
      finalize(() => {
        this.mfaEvaluationInFlight = false;
        this.hasCheckedMfa = true;
      })
    );
  }

  private applyMfaDialogConfig(
    dialogRef: ScaffoldDialogRef,
    data: { initialView?: string; requireSetup?: boolean }
  ) {
    const instance = dialogRef.componentInstance;
    if (typeof instance?.syncDialogConfig === 'function') {
      instance.syncDialogConfig(data);
      dialogRef.contentComponentRef?.changeDetectorRef.detectChanges();
    }
  }

  private openRequiredMfaSetupDialog(options: {
    initialView: 'initial' | 'mfa';
    allowOnVerifyRoute?: boolean;
    suppressAppShellBlock?: boolean;
  }): ScaffoldDialogRef | undefined {
    if (!options.allowOnVerifyRoute && this.isOnVerifyPhoneAndEmailRoute()) {
      return;
    }
    if (!options.suppressAppShellBlock) {
      this.setLoginMfaSetupRequired(true);
    }
    const dialogData = {
      initialView: options.initialView,
      requireSetup: true,
    };
    const dialogRef = this.dialog.open(MfaModalComponent, {
      backdropClass: 'overlay-darkened',
      context: { data: dialogData },
      hasBackdrop: true,
      closeOnBackdropClick: false,
      closeOnEsc: false,
    } as any);
    this.applyMfaDialogConfig(dialogRef, dialogData);
    dialogRef?.onClose?.pipe(take(1)).subscribe(() => {
      if (this.shouldReopenRequiredMfaDialog(options)) {
        setTimeout(() => this.openRequiredMfaSetupDialog(options), 0);
      }
    });
    return dialogRef;
  }

  private shouldReopenRequiredMfaDialog(options: {
    initialView: 'initial' | 'mfa';
    allowOnVerifyRoute?: boolean;
    suppressAppShellBlock?: boolean;
  }): boolean {
    if (this.isUserMfaSet()) {
      return false;
    }
    if (options.suppressAppShellBlock) {
      return this.isOnVerifyPhoneAndEmailRoute();
    }
    return this.isLoginMfaSetupBlocking();
  }

  private openLoginRequiredMfaDialog(allowOnVerifyRoute = false) {
    this.openRequiredMfaSetupDialog({ initialView: 'initial', allowOnVerifyRoute });
  }

  private resetMfaEligibilityState() {
    this.hasCheckedMfa = false;
    this.mfaEvaluationInFlight = false;
    this.userPoolMfaOptional$ = null;
  }

  private getUserPoolMfaOptional$(): Observable<boolean> {
    if (!this.userPoolMfaOptional$) {
      this.userPoolMfaOptional$ = this.http
        .request('get', environment.apiRoot + '/auth/checkuserpoolmfa')
        .pipe(
          catchError(() => of(null)),
          map(response => response === 'OPTIONAL'),
          shareReplay(1)
        );
    }
    return this.userPoolMfaOptional$;
  }

  private ensureRoleInformationForMfa$(): Observable<ScaffoldUser | null> {
    if (this._roleInformation) {
      return of(this._roleInformation);
    }
    if (this._roleDetailsLoaded && !this._roleInformation) {
      this._roleDetailsLoaded = false;
    }
    if (this.roleDetailsLoader) {
      return this._roleDetails.asObservable().pipe(
        take(1),
        timeout(10000),
        catchError(() => of(null))
      );
    }
    return this.http
      .get<ScaffoldUser>(environment.apiRoot + '/users/self?role_details=true')
      .pipe(
        tap(data => {
          this._roleDetailsLoaded = true;
          this._roleInformation = data;
          this._roleDetails.next(data);
          this.maybeCheckForMfaEnabledAfterRoleLoad();
        }),
        catchError(err => {
          console.log(err);
          this._roleDetailsLoaded = false;
          this._roleInformation = null;
          return of(null);
        })
      );
  }

  private isSsoEnabled$(): Observable<boolean> {
    return this.injector
      .get(CognitoDomainService)
      .getCognitoSsoUrl$()
      .pipe(
        take(1),
        map(ssoUrl => !!ssoUrl)
      );
  }

  private getMfaSetupEligibility$(options?: {
    treatCheckFailureAsNeedsSetup?: boolean;
  }): Observable<boolean> {
    if (this.iframeService.isInIframe) {
      return of(false);
    }
    return this.getUserPoolMfaOptional$().pipe(
      switchMap(poolOptional => {
        if (!poolOptional) {
          return of(false);
        }
        return this.ensureRoleInformationForMfa$().pipe(
          switchMap(role => {
            if (!role) {
              return of(false);
            }
            if (this.isUserMfaSet()) {
              return of(false);
            }
            const mfaSet =
              this.getUserInfo()?.mfa_set ?? role?.data?.mfa_set;
            if (mfaSet === false) {
              return of(true);
            }
            return this.http
              .get(environment.apiRoot + '/auth/checkusermfa')
              .pipe(
                catchError(error => of({ error })),
                map((response: any) => {
                  if (response && response.hasOwnProperty('enabled')) {
                    this.updateUser$({ mfa_set: response.enabled }).subscribe();
                  }
                  if (response?.enabled === false) {
                    return true;
                  }
                  if (
                    options?.treatCheckFailureAsNeedsSetup &&
                    (response?.error ||
                      !response ||
                      !response.hasOwnProperty('enabled'))
                  ) {
                    return !this.isUserMfaSet();
                  }
                  return false;
                })
              );
          })
        );
      })
    );
  }

  checkForMfaEnabled(options?: { force?: boolean }) {
    if (!options?.force && this.isOnVerifyPhoneAndEmailRoute()) {
      return;
    }
    if (this.iframeService.isInIframe) {
      this.hasCheckedMfa = true;
      this.setLoginMfaSetupRequired(false);
      return;
    }
    if (options?.force) {
      this.resetMfaEligibilityState();
    }
    if (this.hasCheckedMfa || this.mfaEvaluationInFlight) {
      return;
    }
    this.mfaEvaluationInFlight = true;
    const allowOnVerifyRoute = !!options?.force;
    this.isSsoEnabled$()
      .pipe(
        switchMap(isSsoEnabled =>
          isSsoEnabled ? of(false) : this.getMfaSetupEligibility$()
        ),
        take(1),
        finalize(() => {
          this.mfaEvaluationInFlight = false;
          this.hasCheckedMfa = true;
        })
      )
      .subscribe(needsSetup => {
        if (needsSetup) {
          this.openLoginRequiredMfaDialog(allowOnVerifyRoute);
          return;
        }
        this.setLoginMfaSetupRequired(false);
      });
  }

  getAuthenitcation(): Observable<any> {
    //only try to load once;
    if (!this.accessToken) {
      if (!this.accessLoading) {
        this.accessLoading = true;
        this.http
          .post(environment.apiRoot + '/auth/authentication', {
            purpose: 'connection',
          })
          .subscribe((data: any) => {
            if (data?.error) {
              this.notifications.popNotification('Error', data?.message);
              this.resetUserAuthDetails();
              this.router.navigate(['/login']);
              return;
            }

            this.startRefreshTokenTimer();
            this.accessToken = data.accessToken;
            this.sessionDetails = {
              userAttributes: data.cognitoUserAttributes,
            };
            this.authSub.next(this.accessToken);
          });
      }
    } else {
      this.authSub.next(this.accessToken);
    }
    return this.authSub.asObservable();
  }

  updateUser$(data) {
    return this.http
      .request<ArrayBuffer>('PUT', `${environment.apiRoot}/users/self`, {
        body: { data },
      })
      .pipe(
        tap((response: any) => {
          if (response?.updated) {
            this.setUserInfo({ ...this.userInfo, ...data });
          }
        })
      );
  }

  getUser(id) {
    return this.http.request(
      'get',
      environment.apiRoot +
        '/users/' +
        id +
        '?output=dataonly&mode=list&key_values=cognito_username'
    );
  }

  getUserByCognito(id, instance, env) {
    return this.http.request(
      'get',
      environment.apiRoot +
        '/users/cognito?cognitoId=' +
        id +
        '&output=dataonly&mode=list&key_values=cognito_username,role,email,last_name,first_name&sub_domain=' +
        instance +
        '&environment=' +
        env
    );
  }

  /** Check if in an iframe */
  inIframe(): boolean {
    try {
      console.log(window.self !== window.top);
      return window.self !== window.top;
    } catch (e) {
      console.log('*******************************************', e);
      return true;
    }
  }

  deleteUserData(id, sub_domain, env) {
    console.log(
      'DELETE',
      environment.apiRoot +
        '/users/' +
        id +
        '?sub_domain=' +
        sub_domain +
        '&environment=' +
        env
    );
    return this.http.delete(
      environment.apiRoot +
        '/users/' +
        id +
        '/single?sub_domain=' +
        sub_domain +
        '&environment=' +
        env
    );
  }

  signInWithPasskey(username: string): Observable<any> {
    const url = '/auth/signinpasskey';

    return this.http.post(environment.apiRoot + url, { username });
  }

  handleWebAuthnChallenge(
    username: string,
    challengeData: any
  ): Observable<any> {
    const result = new Subject<any>();

    const challenge = JSON.parse(
      challengeData.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS
    )?.challenge;
    const session = challengeData.Session;

    // convert the base64 challenge to an ArrayBuffer
    const challengeBuffer = this._base64URLToArrayBuffer(challenge);
    const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions =
      {
        challenge: challengeBuffer,
        timeout: 60000, // 1 minute timeout
        rpId: window.location.hostname,
        userVerification: 'preferred',
        // Leave allowCredentials empty to allow any credential
        allowCredentials: [],
      };

    // get the credential from the browser
    navigator.credentials
      .get({
        publicKey: publicKeyCredentialRequestOptions,
      })
      .then(credential => {
        if (!credential || !(credential instanceof PublicKeyCredential)) {
          result.error(new Error('No credential returned'));
          return;
        }

        const publicKeyCredential = credential as PublicKeyCredential;
        const response =
          publicKeyCredential.response as AuthenticatorAssertionResponse;

        // format credential in a serializable format
        const credentialJSON = {
          id: publicKeyCredential.id,
          rawId: this._arrayBufferToBase64URL(publicKeyCredential.rawId),
          response: {
            authenticatorData: this._arrayBufferToBase64URL(
              response.authenticatorData
            ),
            clientDataJSON: this._arrayBufferToBase64URL(
              response.clientDataJSON
            ),
            signature: this._arrayBufferToBase64URL(response.signature),
            userHandle: response.userHandle
              ? this._arrayBufferToBase64URL(response.userHandle)
              : null,
          },
          type: publicKeyCredential.type,
        };

        const authPayload = {
          username,
          session,
          credential: JSON.stringify(credentialJSON),
        };

        return this.http
          .post(environment.apiRoot + '/auth/verifypasskey', authPayload)
          .pipe(catchError(error => of({ error })))
          .subscribe((response: any) => {
            this.handleWebAuthnResponse(response, result);
          });
      })
      .catch(error => {
        console.error('WebAuthn API error:', error);
        result.error(error);
      });

    return result.asObservable();
  }

  private _base64URLToArrayBuffer(base64URL: string): ArrayBuffer {
    // Convert base64URL to base64
    const base64 = base64URL.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const base64Padded = base64 + padding;

    // Convert to binary string
    const binaryString = window.atob(base64Padded);
    const bytes = new Uint8Array(binaryString.length);

    // Fill the bytes array
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
  }

  private _arrayBufferToBase64URL(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';

    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    // Convert to base64
    const base64 = window.btoa(binary);

    // Convert to base64URL by replacing characters
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private handleWebAuthnResponse(response: any, result: Subject<any>): void {
    if (response.status === 'error') {
      result.error(response.message);
      return;
    }

    if (response.access) {
      this.cookieService.set('authenticated_sso', 'false', null, '/');
      this.cookieService.set('active_session', 'true', null, '/');
      this.startRefreshTokenTimer();
      this.accessToken = response.access;
    }

    if (response?.details?.userInfo) {
      this.loggedIn = true;
      Object.keys(response.details.userInfo.data || {}).forEach(key => {
        if (this.mapFields[key]) {
          response.details.userInfo.data[this.mapFields[key]] =
            this.mapFields[key];
          delete response.details.userInfo.data[key];
        }
      });
      this.setUserInfo(response.details.userInfo);
    }

    result.next(response);
    result.complete();
  }

  startPasskeyRegistration(): Observable<any> {
    return this.http
      .post(environment.apiRoot + '/auth/registerpasskey', {})
      .pipe(
        catchError(error => {
          console.error('Error starting passkey registration:', error);
          return of({ error });
        })
      );
  }

  completePasskeyRegistration(credential): Observable<any> {
    if (!credential) {
      return of({ error: { message: 'Missing required credential data' } });
    }

    return this.http
      .post(environment.apiRoot + '/auth/completepasskeyregistration', {
        credential,
      })
      .pipe(
        tap((response: any) => {
          if (response?.status === 'error') {
            this.notifications.popNotification('Error', 'Unknown Error');
          }
        }),
        catchError(error => {
          console.error('Error completing passkey registration:', error);
          return of({ error });
        })
      );
  }

  handleWebAuthnFlow(username: string, challengeData: any): Observable<any> {
    // Ensure the data is in the expected format
    if (
      !challengeData ||
      !challengeData.challengeName ||
      !challengeData.challengeParameters ||
      !challengeData.session
    ) {
      return of({ error: { message: 'Invalid challenge data format' } });
    }

    return this.handleWebAuthnChallenge(username, challengeData);
  }

  authenticateWithWebAuthn(username: string): Observable<any> {
    // initiate authentication and get the challenge
    return this.http
      .post(environment.apiRoot + '/auth/signinpasskey', { username })
      .pipe(
        switchMap(response => this.handleWebAuthnFlow(username, response)),
        catchError(error => {
          console.error('Authentication error:', error);
          return of({ error });
        })
      );
  }
}
